import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';
import { supabase } from '@/integrations/supabase/client';
import { parseMcLimitBlocked, stripMcLimitMarker } from '@/lib/mcLimitMarker';
import McLimitNotice from '@/components/McLimitNotice';

interface SyncProgressDialogProps {
  open: boolean;
  merchantCenterCount?: number;
  feedCount?: number;
  googleAccountId?: string;
  onComplete?: () => void;
}

/**
 * google_accounts.sync_message is rendered verbatim to the user. Most values are
 * legacy English sentences written by fetch-account-data; those keep passing through
 * unchanged. New messages are stable CODES instead, so they can be localized here.
 *
 * Exported for the test: the mapping only earns its keep if the code never leaks.
 */
export const SYNC_MESSAGE_CODES: Record<string, string> = {
  GOOGLE_REAUTH_REQUIRED: 'sync.reauthRequired',
  // Token refresh failed for a reason the user cannot act on (Google outage, or our
  // own client credentials being wrong). Deliberately does NOT say "reconnect".
  GOOGLE_TOKEN_UNAVAILABLE: 'sync.tokenUnavailable',
  // Mitgliedschaft Stufe 1: kein Mitglied dieser Merchant Center hat eine lebende
  // Google-Verbindung (GRANT_UNAVAILABLE_CODE, _shared/google-auth.ts).
  //
  // HEUTE schreibt kein Pfad diesen Code nach `google_accounts.sync_message`:
  // fetch-account-data holt seinen Token ueber `getValidAccessToken(googleAccountId)`
  // und kann darum nur TokenRefreshError sehen. Der Eintrag steht trotzdem hier,
  // weil die drei Marker EINE Familie sind (dieselbe Konstanten-Gruppe im Backend,
  // dieselben zwei Zieltabellen) und der Nachtrag sonst genau dann vergessen wird,
  // wenn ein kuenftiger MC-Pfad hier landet -- der Fallback gibt unbekannte Codes
  // roh zurueck, ein fehlender Eintrag faellt also niemandem auf.
  GOOGLE_GRANT_UNAVAILABLE: 'sync.grantUnavailable',
};

export function localizeSyncMessage(
  message: string,
  t: (key: string) => string,
): string {
  const key = SYNC_MESSAGE_CODES[message];
  return key ? t(key) : message;
}

/**
 * Anzeige-Fassung einer sync_message: erst den maschinenlesbaren
 * `mc_limit_blocked:<n>`-Marker entfernen (der wird separat als lokalisierte
 * Hinweiszeile gerendert, siehe McLimitNotice), dann die Code-Lokalisierung.
 * Strip zuerst, damit ein exakter Code auch dann noch matcht, wenn ein
 * kuenftiger Server-Pfad den Marker an einen Code anhaengen sollte.
 *
 * Exported for the test, wie localizeSyncMessage.
 */
export function displaySyncMessage(
  message: string,
  t: (key: string) => string,
): string {
  return localizeSyncMessage(stripMcLimitMarker(message), t);
}

const SyncProgressDialog: React.FC<SyncProgressDialogProps> = ({
  open,
  merchantCenterCount = 0,
  feedCount = 0,
  googleAccountId,
  onComplete
}) => {
  const { t } = useTranslation('wizard');
  const [currentProgress, setCurrentProgress] = useState(0);
  const [status, setStatus] = useState<string>('connecting');
  const [customMessage, setCustomMessage] = useState<string | null>(null);
  const [activityLogs, setActivityLogs] = useState<string[]>([]); // Track all status messages
  const [startTime, setStartTime] = useState<number>(0);

  // Stable ref for onComplete to avoid resetting the polling effect
  // when the parent re-renders with a new inline callback reference.
  // Without this, the polling interval and completion timeout get cleared
  // on every parent re-render, preventing onComplete from ever firing.
  const onCompleteRef = React.useRef(onComplete);
  React.useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!open) {
      setCurrentProgress(0);
      setStatus('connecting');
      setCustomMessage(null);
      setActivityLogs([]);
      setStartTime(0);
      return;
    }

    // Track when dialog opened to ensure minimum display time
    const dialogOpenTime = Date.now();
    if (startTime === 0) {
      setStartTime(dialogOpenTime);
    }

    // If googleAccountId is provided, poll for sync status
    if (googleAccountId) {
      let completionTimeout: ReturnType<typeof setTimeout> | null = null;
      const seenMessages = new Set<string>();

      const interval = setInterval(async () => {
        const { data } = await supabase
          .from('google_accounts')
          .select('sync_status, sync_message, sync_progress')
          .eq('id', googleAccountId)
          .single();

        if (data) {
          setCurrentProgress(data.sync_progress || 0);
          setStatus(data.sync_status);

          // Track activity logs - add new messages only (using local Set to avoid stale closure)
          if (data.sync_message && !seenMessages.has(data.sync_message)) {
            seenMessages.add(data.sync_message);
            setActivityLogs(prev => [...prev, data.sync_message]);
          }

          if (data.sync_message) {
            setCustomMessage(data.sync_message);
          }

          // Stop polling when done or failed
          if (data.sync_status === 'complete' || data.sync_status === 'failed') {
            clearInterval(interval);

            if (data.sync_status === 'complete') {
              // Ensure minimum display time of 1500ms before calling onComplete
              const elapsed = Date.now() - dialogOpenTime;
              const remainingTime = Math.max(0, 1500 - elapsed);
              completionTimeout = setTimeout(() => {
                onCompleteRef.current?.();
              }, remainingTime);
            }
            // On 'failed': leave dialog open showing the error message — caller handles retry
          }
        }
      }, 1000);

      return () => {
        clearInterval(interval);
        if (completionTimeout) clearTimeout(completionTimeout);
      };
    } else {
      // Fallback to old behavior if no googleAccountId
      if (merchantCenterCount === 0) {
        setStatus('connecting');
        setCurrentProgress(20);
      } else if (feedCount === 0) {
        setStatus('fetching');
        setCurrentProgress(40);
      } else {
        setStatus('loading_feeds');
        setCurrentProgress(Math.min(60 + (feedCount * 2), 95));
      }
    }
  }, [open, merchantCenterCount, feedCount, googleAccountId]);

  // MC-Anzahl-Gate: der Marker haengt an der Abschlussmeldung des Syncs.
  // Statt ihn roh anzuzeigen, wird er entfernt (displaySyncMessage) und hier
  // als eigene, lokalisierte Hinweiszeile mit Add-on-CTA gerendert.
  const mcBlockedCount = parseMcLimitBlocked(customMessage);

  const getStatusMessage = () => {
    if (customMessage) return displaySyncMessage(customMessage, t);

    switch (status) {
      case 'pending':
        return t('sync.connectingGoogle');
      case 'fetching_accounts':
        return t('sync.fetchingMerchants');
      case 'registering':
        return t('sync.registering');
      case 'awaiting_setup':
        return t('sync.registrationSuccess');
      case 'fetching':
        return t('sync.fetchingData');
      case 'complete':
        return t('sync.setupComplete');
      case 'failed':
        return t('sync.setupFailed');
      case 'connecting':
        return t('sync.connectingGoogle');
      case 'loading_feeds':
        return t('sync.loadingFeeds', { count: feedCount });
      default:
        return t('sync.processing');
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-lg [&>button]:hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Spinner size="md" />
            {status === 'complete'
              ? t('sync.completeTitle')
              : status === 'failed'
              ? t('sync.failedTitle', { defaultValue: 'Setup Failed' })
              : t('sync.syncingTitle')}
          </DialogTitle>
          <DialogDescription>
            {status === 'complete'
              ? t('sync.completeDesc')
              : status === 'failed'
              ? t('sync.failedDesc', { defaultValue: 'Something went wrong. Please try again.' })
              : t('sync.syncingDesc')
            }
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-medium text-foreground">
                      {getStatusMessage()}
                    </p>
                    <span className="text-sm font-medium text-primary">
                      {currentProgress}%
                    </span>
                  </div>
                  <Progress value={currentProgress} variant="gradient" className="h-2.5" />
                </div>
              </div>
              
              {/* Activity Log */}
              {activityLogs.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">{t('sync.activityLog')}</p>
                  <ScrollArea className="h-32 rounded-lg border bg-muted/30 p-3">
                    <div className="space-y-1.5">
                      {activityLogs.map((log, index) => (
                        <div key={index} className="text-xs flex items-start gap-2">
                          <span className="text-primary mt-0.5">•</span>
                          <span className="flex-1 text-foreground/80">{displaySyncMessage(log, t)}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
          </div>

          {mcBlockedCount !== null && (
            <McLimitNotice blockedCount={mcBlockedCount} />
          )}

          {merchantCenterCount > 0 && (
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="text-center p-3 rounded-lg bg-primary/5 border border-primary/10">
                <p className="text-2xl font-bold text-primary">{merchantCenterCount}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('sync.merchantCenters')}</p>
              </div>
              {feedCount > 0 && (
                <div className="text-center p-3 rounded-lg bg-primary/5 border border-primary/10">
                  <p className="text-2xl font-bold text-primary">{feedCount}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t('sync.feeds')}</p>
                </div>
              )}
            </div>
          )}
          
          <p className="text-xs text-muted-foreground text-center">
            {t('sync.mayTakeMoment')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SyncProgressDialog;
